import { ref, computed, isRef } from "vue";
import services from "@/services";

export function useUsage() {
  const errorMessage = ref("");
  const data = ref(null);
  const loading = ref(false);

  const fetchCurrentUsage = async () => {
    loading.value = true;
    errorMessage.value = "";
    try {
      const res = await services.usage.getCurrentUsage();
      data.value = res;
    } catch (error) {
      errorMessage.value = "Failed to fetch current usage.";
      data.value = null;
    } finally {
      loading.value = false;
    }
  };

  const fetchOrgUsage = async (orgId) => {
    if (!orgId) {
      errorMessage.value = "Organization ID is required";
      return;
    }
    loading.value = true;
    errorMessage.value = "";
    try {
      const res = await services.usage.getOrgUsage(orgId);
      data.value = res;
    } catch (error) {
      errorMessage.value = "Failed to fetch organization usage.";
      data.value = null;
    } finally {
      loading.value = false;
    }
  };

  return {
    data,
    loading,
    errorMessage,
    fetchCurrentUsage,
    fetchOrgUsage,
  };
}

export function deriveUsageMetrics(usageOrRef, loadingOrRef) {
  const usageRef = isRef(usageOrRef) ? usageOrRef : ref(usageOrRef ?? null);
  const loadingRef = isRef(loadingOrRef) ? loadingOrRef : ref(!!loadingOrRef);

  const isLoading = computed(() => !!loadingRef.value || usageRef.value === null);

  const orgAllowance = computed(() => {
    const v = usageRef.value?.organization?.allowance;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

  const orgRemaining = computed(() => {
    const v = usageRef.value?.organization?.remaining;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });

  const hasUsageData = computed(() => {
    const hasOrg = !!usageRef.value?.organization;
    const hasNumbers = orgAllowance.value !== null || orgRemaining.value !== null;
    const cyc = usageRef.value?.cycle;
    const hasCycle = !!(cyc && (cyc.start || cyc.endExclusive));
    return hasOrg && (hasNumbers || hasCycle);
  });

  const limit = computed(() => orgAllowance.value ?? 0);
  const remaining = computed(() => orgRemaining.value ?? 0);

  const viewed = computed(() => {
    const v = usageRef.value?.me?.distinctBusinesses;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  });

  const cycleEndExclusive = computed(() => usageRef.value?.cycle?.endExclusive ?? null);

  return { 
    isLoading, 
    hasUsageData, 
    limit, 
    remaining, 
    viewed, 
    cycleEndExclusive 
  };
}

export function upgrade() {
  window.location.href = "mailto:support@betascore.ai";
}
